import { Controller, useFormContext } from 'react-hook-form'

import { CustomFieldRestriction } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  FieldHeader,
  OptionProps,
  SelectField as SelectFieldComponent,
} from '@avantstay/backoffice-core'

import { useSelectMenuPlacement } from './useSelectMenuPlacement'

interface TaxonomyNodeSelectFieldProps<NodeType> {
  label: string
  formName: string
  options: OptionProps<NodeType>[]
  onNodeChange: (node?: NodeType) => void
  getNodeLabel: (node: NodeType) => string
  isDisabled?: boolean
  isRequired?: boolean
  autoMenuPlacement?: boolean
}

export function TaxonomyNodeSelectField<NodeType>({
  label,
  formName,
  options,
  onNodeChange,
  getNodeLabel,
  isDisabled,
  isRequired = true,
  autoMenuPlacement = false,
}: TaxonomyNodeSelectFieldProps<NodeType>) {
  const { control } = useFormContext()
  const { menuPlacement, onMenuClick } = useSelectMenuPlacement(autoMenuPlacement)

  return (
    <FieldHeader.Default
      title={label}
      restriction={isRequired ? CustomFieldRestriction.required : CustomFieldRestriction.optional}
      marginBottom={24}
    >
      <Controller
        control={control}
        name={formName}
        rules={{ required: isRequired ? `${label} field is required` : false }}
        render={({ field: { value }, fieldState }) => {
          const selectedNode = value as NodeType | null | undefined
          const selectedOption = selectedNode
            ? (options.find(option => option.value === selectedNode) ?? {
                label: getNodeLabel(selectedNode),
                value: selectedNode,
              })
            : null

          return (
            <SelectFieldComponent.Default
              options={options}
              menuPlacement={menuPlacement}
              onClick={onMenuClick}
              placeholder="Select..."
              error={fieldState?.error?.message}
              isDisabled={isDisabled || !options.length}
              onChange={(selected: OptionProps<NodeType>) => onNodeChange(selected.value)}
              value={selectedOption}
            />
          )
        }}
      />
    </FieldHeader.Default>
  )
}
